import { useEffect, useState, useCallback, type FormEvent } from 'react';
import { useAdmin } from '../../components/AdminLayout';
import { api, type AuditEntry } from '../../lib/api';
import s from './AdminAudit.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Filters {
  tenant: string;
  user: string;
  action: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: Filters = {
  tenant: '',
  user: '',
  action: '',
  dateFrom: '',
  dateTo: '',
};

const PAGE_SIZE = 25;

// Extended audit entry type for admin (may include extra fields)
interface AdminAuditEntry extends AuditEntry {
  tenantId?: string;
  tenantName?: string;
  ip?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminAudit() {
  const { secret } = useAdmin();

  const [entries, setEntries] = useState<AdminAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  const fetchAudit = useCallback(
    async (currentPage: number, currentFilters: Filters) => {
      setLoading(true);
      setError(null);

      try {
        const params: Record<string, any> = {
          page: currentPage,
          limit: PAGE_SIZE,
        };

        if (currentFilters.action) params.action = currentFilters.action;
        if (currentFilters.user) params.userId = currentFilters.user;
        if (currentFilters.tenant) params.tenantId = currentFilters.tenant;
        if (currentFilters.dateFrom) params.dateFrom = currentFilters.dateFrom;
        if (currentFilters.dateTo) params.dateTo = currentFilters.dateTo;

        const res = await api.admin.audit(secret, params);
        setEntries((res.data ?? []) as AdminAuditEntry[]);
        setTotal(res.total ?? 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load audit log');
      } finally {
        setLoading(false);
      }
    },
    [secret],
  );

  // Initial fetch & on page/filter change
  useEffect(() => {
    fetchAudit(page, appliedFilters);
  }, [page, appliedFilters, fetchAudit]);

  function handleApplyFilters(e: FormEvent) {
    e.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  }

  function handleClearFilters() {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  }

  function formatTimestamp(iso: string): string {
    try {
      return new Date(iso).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  function formatDetails(details?: Record<string, unknown>): string {
    if (!details) return '\u2014';
    try {
      return JSON.stringify(details);
    } catch {
      return '\u2014';
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={s.page}>
      <h1 className={s.heading}>Audit Log</h1>

      {/* Filters */}
      <form className={s.filters} onSubmit={handleApplyFilters}>
        <div className={s.filterField}>
          <label className={s.filterLabel}>Tenant</label>
          <input
            type="text"
            className={s.filterInput}
            placeholder="Tenant ID or name"
            value={filters.tenant}
            onChange={(e) => setFilters((f) => ({ ...f, tenant: e.target.value }))}
          />
        </div>

        <div className={s.filterField}>
          <label className={s.filterLabel}>User</label>
          <input
            type="text"
            className={s.filterInput}
            placeholder="User ID or email"
            value={filters.user}
            onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}
          />
        </div>

        <div className={s.filterField}>
          <label className={s.filterLabel}>Action</label>
          <input
            type="text"
            className={s.filterInput}
            placeholder="e.g. tenant.update"
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          />
        </div>

        <div className={s.filterField}>
          <label className={s.filterLabel}>From</label>
          <input
            type="date"
            className={s.filterInput}
            value={filters.dateFrom}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          />
        </div>

        <div className={s.filterField}>
          <label className={s.filterLabel}>To</label>
          <input
            type="date"
            className={s.filterInput}
            value={filters.dateTo}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
          />
        </div>

        <div className={s.filterBtnGroup}>
          <button type="submit" className={s.btnFilter}>
            Apply
          </button>
          <button type="button" className={s.btnClear} onClick={handleClearFilters}>
            Clear
          </button>
        </div>
      </form>

      {error && <div className={s.error}>{error}</div>}

      {/* Table */}
      {loading ? (
        <div className={s.loading}>Loading audit log&hellip;</div>
      ) : entries.length === 0 ? (
        <div className={s.empty}>No audit entries found.</div>
      ) : (
        <>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Tenant</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>IP</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <span className={s.timestamp}>
                        {formatTimestamp(entry.createdAt)}
                      </span>
                    </td>
                    <td>{entry.tenantName ?? entry.tenantId ?? '\u2014'}</td>
                    <td>{entry.userEmail}</td>
                    <td className={s.actionCell}>{entry.action}</td>
                    <td>
                      {entry.resource}
                      {entry.resourceId ? ` (${entry.resourceId})` : ''}
                    </td>
                    <td>
                      <span className={s.ipCell}>{entry.ip ?? '\u2014'}</span>
                    </td>
                    <td>
                      <span className={s.detailsCell} title={formatDetails(entry.details)}>
                        {formatDetails(entry.details)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={s.pagination}>
            <div className={s.pageInfo}>
              Showing {(page - 1) * PAGE_SIZE + 1}&ndash;
              {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()} entries
            </div>
            <div className={s.pageButtons}>
              <button
                type="button"
                className={s.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                &laquo; Prev
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                // Show pages around current page
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    className={pageNum === page ? s.pageBtnActive : s.pageBtn}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                type="button"
                className={s.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next &raquo;
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
